import { useState, useCallback, useEffect, useRef } from "react";

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const isStringArray = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((item) => typeof item === "string");

/**
 * Returns the persisted string list, or `undefined` when nothing has been
 * persisted yet. Distinguishing "nothing stored" from "explicitly empty"
 * lets consumers with non-empty defaults keep a user's cleared preference.
 */
export const readStringArrayFromStorage = (
    key: string,
): readonly string[] | undefined => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) {
            return undefined;
        }
        const parsed = JSON.parse(raw);
        if (isStringArray(parsed)) {
            return parsed;
        }
    } catch {
        // Corrupt / unavailable storage — fall back to defaults.
    }
    return undefined;
};

export const writeStringArrayToStorage = (
    key: string,
    values: readonly string[],
) => {
    try {
        localStorage.setItem(key, JSON.stringify(values));
    } catch {
        // Storage unavailable — skip silently.
    }
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Generic hook that tracks which spreadsheet column fields are hidden.
 * Field identity matches `ColumnDef.field`.
 *
 * When `storageKey` is provided the hidden-field set is persisted to
 * `localStorage` so the user's preference survives page reloads and
 * drawer open/close cycles.
 *
 * @param defaultHiddenFields - Fields hidden on first render (used as the
 *   reset target).
 * @param storageKey - Optional `localStorage` key for persistence.
 */
export function useSpreadsheetColumnVisibility(
    defaultHiddenFields: readonly string[] = [],
    storageKey?: string,
) {
    const [hiddenFields, setHiddenFields] = useState<ReadonlySet<string>>(
        () => {
            if (storageKey) {
                const stored = readStringArrayFromStorage(storageKey);
                if (stored) {
                    return new Set(stored);
                }
            }
            return new Set(defaultHiddenFields);
        },
    );

    const toggleField = useCallback((field: string) => {
        setHiddenFields((prev) => {
            const next = new Set(prev);
            if (next.has(field)) {
                next.delete(field);
            } else {
                next.add(field);
            }
            return next;
        });
    }, []);

    const reset = useCallback(() => {
        setHiddenFields(new Set(defaultHiddenFields));
    }, [defaultHiddenFields]);

    // Persist changes outside the state updater — updaters must stay pure so
    // React can safely invoke them twice in Strict Mode.
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (storageKey) {
            writeStringArrayToStorage(storageKey, [...hiddenFields]);
        }
    }, [hiddenFields, storageKey]);

    return { hiddenFields, toggleField, reset };
}
